import { MathUtils, Matrix3, Mesh, Vector3 } from 'three';

export interface UVBounds {
  uMin: number;
  uMax: number;
  vMin: number;
  vMax: number;
}

export interface UVPoint {
  u: number;
  v: number;
}

export interface FacePanel {
  // The DAE group name this panel was built from (e.g. "Face0") - the join key into a
  // /project/panel-names/{type}.json map, since that map is keyed the same way.
  name: string;
  normal: Vector3;
  centroid: Vector3;
  meshes: { mesh: Mesh; originalMaterial: Mesh['material'] }[];
  uvBounds: UVBounds;
  // The panel's true printable-area outline (straight edges, curved edges, notches, and separate
  // disconnected pieces - whatever the mesh actually has), one closed loop per disconnected region
  // - see computeFaceBoundaryUV(). undefined when the geometry's boundary edges didn't resolve
  // cleanly (a branch point/non-manifold edge); callers fall back to uvBounds's rectangle then.
  boundaryUV?: UVPoint[][];
}

// The shared outside material's texture IS the Fabric canvas (see phase 3a), so each Face's UV
// coordinates map directly onto canvas pixel space - clicking a point on the 2D canvas can be
// resolved back to "which physical panel is this" by finding whose UV bounding box contains it.
export function computeFaceUVBounds(outsideMeshes: Mesh[]): UVBounds {
  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;
  outsideMeshes.forEach((mesh) => {
    const uvAttr = mesh.geometry.attributes['uv'];
    if (!uvAttr) {
      return;
    }
    for (let i = 0; i < uvAttr.count; i++) {
      const u = uvAttr.getX(i);
      const v = uvAttr.getY(i);
      uMin = Math.min(uMin, u);
      uMax = Math.max(uMax, u);
      vMin = Math.min(vMin, v);
      vMax = Math.max(vMax, v);
    }
  });
  return { uMin, uMax, vMin, vMax };
}

// UV coordinates that round to the same key here are treated as the same point - confirmed against
// a real DAE (two adjoining outside mesh pieces on the same Face share bit-identical UVs at their
// seam), so this only needs to absorb ordinary floating-point noise, not near-miss coordinates.
const BOUNDARY_UV_EPSILON = 1e-5;
const boundaryUVKey = (p: UVPoint): string => `${Math.round(p.u / BOUNDARY_UV_EPSILON)}:${Math.round(p.v / BOUNDARY_UV_EPSILON)}`;

// Derives a panel's true printable-area outline from its mesh triangles, instead of the rectangle
// computeFaceUVBounds() gives - for a hover outline that hugs the real shape (straight edges,
// curved edges, notches, and separate disconnected pieces - whatever the mesh actually has). A
// panel is often more than one mesh piece glued together at a UV seam (confirmed live: a Face's two
// "0OutsideOneUp" pieces can share an edge with bit-identical UVs rather than being one contiguous
// mesh) - so every triangle from every piece is treated as one shared soup, keyed by UV coordinate
// rather than by mesh-local vertex index, which is what lets edges across separate pieces cancel
// out as interior the same way edges within one piece do. An edge walked by exactly one triangle is
// on the outer boundary; one walked by two is interior/seam and drops out. The surviving boundary
// edges are then stitched into ordered loops by simple adjacency, one loop per disconnected
// region - confirmed live: a rectangle plus two small unconnected corner tabs is a real panel shape
// here, not a hypothetical, so this returns each as its own loop rather than forcing everything
// into one walk. That only works cleanly if every boundary point has exactly degree 2 (no branch
// points/non-manifold edges); rather than guess at a resolution for any topology this simple
// approach can't resolve, this bails to undefined and lets the caller fall back to the bounding
// box - a safe, already-correct rectangle - instead of risking a garbled outline.
export function computeFaceBoundaryUV(outsideMeshes: Mesh[]): UVPoint[][] | undefined {
  type Edge = { a: UVPoint; b: UVPoint; count: number };
  const edges = new Map<string, Edge>();

  const addEdge = (a: UVPoint, b: UVPoint): void => {
    const [first, second] = boundaryUVKey(a) <= boundaryUVKey(b) ? [a, b] : [b, a];
    const key = `${boundaryUVKey(first)}|${boundaryUVKey(second)}`;
    const existing = edges.get(key);
    if (existing) {
      existing.count++;
    } else {
      edges.set(key, { a: first, b: second, count: 1 });
    }
  };
  // Twice the signed triangle area - used only for the near-zero check below, so the missing /2
  // (and the sign) don't matter.
  const signedArea2 = (p0: UVPoint, p1: UVPoint, p2: UVPoint): number =>
    (p1.u - p0.u) * (p2.v - p0.v) - (p2.u - p0.u) * (p1.v - p0.v);

  outsideMeshes.forEach((mesh) => {
    const uvAttr = mesh.geometry.attributes['uv'];
    if (!uvAttr) {
      return;
    }
    const pointAt = (i: number): UVPoint => ({ u: uvAttr.getX(i), v: uvAttr.getY(i) });
    // ColladaLoader always resolves polylists down to triangles (three.js has no quad primitive),
    // so every 3 consecutive indices - or every 3 consecutive vertices, if this mesh has no index
    // buffer - are one triangle.
    const index = mesh.geometry.index;
    const triangleCount = Math.floor((index ? index.count : uvAttr.count) / 3);
    for (let t = 0; t < triangleCount; t++) {
      const i0 = index ? index.getX(t * 3) : t * 3;
      const i1 = index ? index.getX(t * 3 + 1) : t * 3 + 1;
      const i2 = index ? index.getX(t * 3 + 2) : t * 3 + 2;
      const p0 = pointAt(i0);
      const p1 = pointAt(i1);
      const p2 = pointAt(i2);
      // Confirmed live on a real Face: the physical box-edge wall (a thin strip with real 3D area)
      // can be UV-mapped to a single line rather than a region - e.g. a mesh piece whose 3 UV
      // corners are only 2 distinct points. Such a triangle has ~zero UV area and no genuine
      // boundary of its own; counting its edges corrupts the real boundary's edge counts (a shared
      // edge that should cancel out as interior instead ends up walked 1 or 3 times), so skip it.
      if (Math.abs(signedArea2(p0, p1, p2)) < 1e-9) {
        continue;
      }
      addEdge(p0, p1);
      addEdge(p1, p2);
      addEdge(p2, p0);
    }
  });

  const boundaryEdges = [...edges.values()]
    .filter((edge) => edge.count === 1)
    .map((edge) => ({ a: edge.a, b: edge.b, used: false }));
  if (boundaryEdges.length < 3) {
    return undefined;
  }

  const edgesAtPoint = new Map<string, (typeof boundaryEdges)[number][]>();
  boundaryEdges.forEach((edge) => {
    [edge.a, edge.b].forEach((p) => {
      const key = boundaryUVKey(p);
      const list = edgesAtPoint.get(key);
      if (list) {
        list.push(edge);
      } else {
        edgesAtPoint.set(key, [edge]);
      }
    });
  });
  // Every point on a clean closed loop has exactly 2 boundary edges touching it - but that alone
  // doesn't rule out a branch point (degree > 2) OR several separate small loops (each internally
  // still degree-2, just disconnected from one another - confirmed live: a rectangle plus two small
  // unconnected corner tabs is a real panel shape here, not a hypothetical). Only degree > 2 can be
  // caught here; telling "one loop" apart from "several disjoint loops" needs the walk below to
  // actually notice it closed early.
  for (const list of edgesAtPoint.values()) {
    if (list.length !== 2) {
      return undefined;
    }
  }

  // Each connected component of a degree-2-everywhere graph is exactly one simple cycle - walk
  // every not-yet-consumed edge's own component to completion (stopping as soon as it returns to
  // its own start, not after a fixed edge count - an earlier version of this walk kept going past
  // that point on a stale, already-used edge, padding a small disjoint sub-loop's result out with
  // repeated points until it coincidentally matched boundaryEdges.length) before moving on to the
  // next not-yet-visited edge, which necessarily belongs to a different component.
  const loops: UVPoint[][] = [];
  for (const startEdge of boundaryEdges) {
    if (startEdge.used) {
      continue;
    }
    const loop: UVPoint[] = [];
    let current = startEdge.a;
    let currentEdge = startEdge;
    const startKey = boundaryUVKey(current);
    let closed = false;
    for (let step = 0; step < boundaryEdges.length; step++) {
      loop.push(current);
      currentEdge.used = true;
      const next = boundaryUVKey(currentEdge.a) === boundaryUVKey(current) ? currentEdge.b : currentEdge.a;
      current = next;
      if (boundaryUVKey(current) === startKey) {
        closed = true;
        break;
      }
      const candidates = edgesAtPoint.get(boundaryUVKey(next)) ?? [];
      const nextEdge = candidates.find((edge) => !edge.used);
      if (!nextEdge) {
        // Shouldn't happen given the degree-2 check above, but bail safely rather than risk a
        // silently wrong shape.
        return undefined;
      }
      currentEdge = nextEdge;
    }
    if (!closed || loop.length < 3) {
      return undefined;
    }
    loops.push(loop);
  }
  return loops;
}

export function computeFaceGeometry(mesh: Mesh): { normal: Vector3; centroid: Vector3 } {
  const geometry = mesh.geometry;
  const positionAttr = geometry.attributes['position'];
  const normalAttr = geometry.attributes['normal'];

  const centroid = new Vector3();
  for (let i = 0; i < positionAttr.count; i++) {
    centroid.add(new Vector3().fromBufferAttribute(positionAttr, i));
  }
  centroid.divideScalar(positionAttr.count);
  mesh.localToWorld(centroid);

  const normal = normalAttr ? new Vector3().fromBufferAttribute(normalAttr, 0) : new Vector3(0, 1, 0);
  const normalMatrix = new Matrix3().getNormalMatrix(mesh.matrixWorld);
  normal.applyMatrix3(normalMatrix).normalize();

  return { normal, centroid };
}

// Fit on whichever axis is tighter (vertical FOV vs. the aspect-derived horizontal FOV), so a
// narrow/tall viewport doesn't crop the sides, and a wide one doesn't crop top/bottom. Split out
// of design-3d.component.ts's fitCameraToAvatar() so compensateZoomForSplit() can re-derive a
// distance for a hypothetical (not-yet-laid-out) aspect ratio too, off the same
// avatarBoundingRadius, without re-measuring the model's geometry.
export function fittedDistanceForAspect(fovDegrees: number, aspect: number, avatarBoundingRadius: number): number {
  const verticalFov = MathUtils.degToRad(fovDegrees);
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * aspect);
  const tightestFov = Math.min(verticalFov, horizontalFov);
  const FIT_MARGIN = 1.2;
  return (avatarBoundingRadius / Math.sin(tightestFov / 2)) * FIT_MARGIN;
}

// Fixed elevation the default view is held at, matching the reference 3/4 angle in
// upload-preview.component.ts (position (4.5, 2, 0) from a target at the origin:
// atan2(2, 4.5) ~= 24 deg) - see [[design3d_default_camera_angle]].
const DEFAULT_PANEL_ELEVATION_RADIANS = Math.atan2(2, 4.5);
// Rotates the horizontal bearing around world up before the elevation is applied, so the
// default view sits a few degrees off dead-on instead of centered on the panel - reveals a
// sliver of the box's right side alongside the front, instead of a flat head-on shot. Positive
// angle here (three.js's Vector3.applyAxisAngle right-hand rule around +Y) is what produces
// "right" - confirmed visually (a new side panel became visible on the right that wasn't before)
// rather than derived, since the sign depends on which way each box style's panel normals face.
const DEFAULT_PANEL_AZIMUTH_TILT_RADIANS = (15 * Math.PI) / 180;

// Positions the camera to instantly face whichever panel has the largest printable area - see
// design-3d.component.ts's faceDefaultPanel()/resetViewToInitialAngle() for the full rationale.
// The panel's raw normal alone puts the camera dead level with that panel; this discards the
// normal's own vertical component and keeps only its horizontal bearing (which side of the box
// we're viewing from), then rebuilds the direction from that bearing at a fixed elevation angle -
// see the original comment history in design-3d.component.ts for why.
export function computeDefaultPanelDirection(facePanels: FacePanel[]): Vector3 | undefined {
  if (!facePanels.length) {
    return undefined;
  }
  const panelArea = (panel: FacePanel): number =>
    (panel.uvBounds.uMax - panel.uvBounds.uMin) * (panel.uvBounds.vMax - panel.uvBounds.vMin);
  const defaultPanel = facePanels.reduce((largest, panel) => (panelArea(panel) > panelArea(largest) ? panel : largest));

  const horizontal = new Vector3(defaultPanel.normal.x, 0, defaultPanel.normal.z);
  // Top/bottom panels have a near-zero horizontal component - fall back to the fedex reference
  // bearing (+X) rather than normalizing a near-zero-length vector into noise.
  const bearing = horizontal.lengthSq() > 1e-6 ? horizontal.normalize() : new Vector3(1, 0, 0);
  bearing.applyAxisAngle(new Vector3(0, 1, 0), DEFAULT_PANEL_AZIMUTH_TILT_RADIANS);
  return bearing
    .multiplyScalar(Math.cos(DEFAULT_PANEL_ELEVATION_RADIANS))
    .add(new Vector3(0, Math.sin(DEFAULT_PANEL_ELEVATION_RADIANS), 0));
}

// mirror accounts for the inside 2D canvas's own pixel layout being a horizontal mirror of the
// outside one - confirmed against a real DAE: the "1InsideOneUp" mesh piece for a Face carries the
// exact same UV values as its "0OutsideOneUp" counterpart (just reversed triangle winding, for the
// backface normal), so a panel's UV footprint alone can't tell outside from inside. The mirror is
// purely a property of which 2D canvas is being read, supplied by the caller (in practice,
// design-3d.component.ts passes activeSide === 'inside', since every caller here already only
// acts on the currently-active side's own canvas/click/hover).
const mirrorU = (u: number, mirror: boolean): number => (mirror ? 1 - u : u);

// Any click on the 2D canvas - empty space or a design object - is resolved to a panel via UV
// bounding boxes. scenePoint is always canvas-absolute (real Fabric mouse events already are;
// callers building a synthetic point pass canvas-absolute fabricImage.left/top too) - the margin
// is subtracted here to land back in the design area's own 0..canvasWidthPx/canvasHeightPx space
// that UV bounds are expressed in.
export function findPanelAt(
  scenePoint: { x: number; y: number },
  canvasWidthPx: number,
  canvasHeightPx: number,
  canvasMargin: number,
  facePanels: FacePanel[],
  mirror = false,
): FacePanel | undefined {
  if (!canvasWidthPx || !canvasHeightPx) {
    return undefined;
  }
  const u = mirrorU((scenePoint.x - canvasMargin) / canvasWidthPx, mirror);
  // CanvasTexture defaults to flipY=true, so UV v=0 is the bottom of the source canvas, not the
  // top - matches the same top/bottom flip already relied on for the design texture itself.
  const v = 1 - (scenePoint.y - canvasMargin) / canvasHeightPx;
  return facePanels.find((p) => u >= p.uvBounds.uMin && u <= p.uvBounds.uMax && v >= p.uvBounds.vMin && v <= p.uvBounds.vMax);
}

// Converts a panel's UV bounds into canvas-pixel bounds (design-area space, canvasMargin-offset)
// - shared by design-3d.component.ts's getFaceBoundsForObject() (align-on-panel) and
// fillPanelAt() (fill tool). Mirroring flips which UV bound lands on which side (uMax becomes the
// left edge, not uMin), not just the values - so left/right stay left <= right either way.
export function getPanelPixelBounds(
  panel: FacePanel,
  canvasWidthPx: number,
  canvasHeightPx: number,
  canvasMargin: number,
  mirror = false,
): { left: number; right: number; top: number; bottom: number } {
  const uLeft = mirror ? mirrorU(panel.uvBounds.uMax, true) : panel.uvBounds.uMin;
  const uRight = mirror ? mirrorU(panel.uvBounds.uMin, true) : panel.uvBounds.uMax;
  return {
    left: uLeft * canvasWidthPx + canvasMargin,
    right: uRight * canvasWidthPx + canvasMargin,
    top: (1 - panel.uvBounds.vMax) * canvasHeightPx + canvasMargin,
    bottom: (1 - panel.uvBounds.vMin) * canvasHeightPx + canvasMargin,
  };
}

// Same UV-to-pixel mapping as getPanelPixelBounds(), applied point-by-point to a panel's true
// boundary loops instead of just its corners - for design-3d.component.ts's hover outline. One
// array per disconnected region of the panel (see computeFaceBoundaryUV()). Returns undefined when
// that couldn't resolve any (caller falls back to the rectangle from getPanelPixelBounds() instead).
export function getPanelBoundaryPixelPoints(
  panel: FacePanel,
  canvasWidthPx: number,
  canvasHeightPx: number,
  canvasMargin: number,
  mirror = false,
): { x: number; y: number }[][] | undefined {
  return panel.boundaryUV?.map((loop) =>
    loop.map((p) => ({
      x: mirrorU(p.u, mirror) * canvasWidthPx + canvasMargin,
      y: (1 - p.v) * canvasHeightPx + canvasMargin,
    })),
  );
}

// Maps a live camera-to-target distance onto the slider's 0-100 range: 0 at maxDistance (zoomed
// all the way out), 100 at minDistance (zoomed all the way in). fallback is returned unchanged
// when minDistance/maxDistance aren't a valid range yet (mirrors design-3d.component.ts's
// percentForDistance(), which falls back to the component's own current cameraZoomPercent).
export function percentForDistance(distance: number, minDistance: number, maxDistance: number, fallback: number): number {
  if (!Number.isFinite(minDistance) || !Number.isFinite(maxDistance) || maxDistance <= minDistance) {
    return fallback;
  }
  const clamped = MathUtils.clamp(distance, minDistance, maxDistance);
  return ((maxDistance - clamped) / (maxDistance - minDistance)) * 100;
}
